import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, DownloadIcon, EllipsisVerticalIcon, PenLineIcon, SendIcon, Trash2Icon } from 'lucide-react'
import {
	ConfirmDialog,
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	IconButton,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	useDeleteDocumentMutation,
	useListPropertiesQuery,
	useListRegistryDocumentsQuery,
	useListRentalsQuery,
	useUpdateDocumentStatusMutation,
} from '@/api'
import { PanelDown } from '@/components/panel-down'
import {
	DEFAULT_LOCALE,
	DOCUMENT_STATUS_KEYS, DOCUMENT_STATUS_VARIANTS,
	DOCUMENT_STATUSES,
	DOCUMENT_TYPE_KEYS,
	DOCUMENT_TYPES,
} from '@/types'
import type { DocumentStatus, DocumentType, RegistryDocument } from '@/types'

export const Route = createFileRoute('/_app/documents/registry')({
	component: DocumentsRegistryPage,
})

const PAGE_SIZE = 10

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function DocumentsRegistryPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all')
	const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')
	const [propertyFilter, setPropertyFilter] = useState<string>('all')
	const [rentalFilter, setRentalFilter] = useState<string>('all')
	const [docToDelete, setDocToDelete] = useState<RegistryDocument | null>(null)

	const { data } = useListRegistryDocumentsQuery({
		type:       typeFilter     === 'all' ? undefined : typeFilter,
		status:     statusFilter   === 'all' ? undefined : statusFilter,
		propertyId: propertyFilter === 'all' ? undefined : propertyFilter,
		rentalId:   rentalFilter   === 'all' ? undefined : rentalFilter,
		search:     search || undefined,
		page,
		limit: PAGE_SIZE,
	})

	const { data: propertiesData } = useListPropertiesQuery()
	const { data: rentalsData }    = useListRentalsQuery()

	const [updateStatus]   = useUpdateDocumentStatusMutation()
	const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation()

	const documents = data?.documents ?? []
	const total = data?.total ?? 0
	const properties = propertiesData?.properties ?? []
	const rentals    = rentalsData?.rentals       ?? []

	function handleDownload(doc: RegistryDocument) {
		if (doc.fileUrl) window.open(doc.fileUrl, '_blank')
	}

	function handleSign(doc: RegistryDocument) {
		void updateStatus({ id: doc.id, body: { status: 'SIGNED_BY_ME' } })
	}

	function handleSend(doc: RegistryDocument) {
		void updateStatus({ id: doc.id, body: { status: 'SENT' } })
	}

	async function handleConfirmDelete() {
		if (!docToDelete) return
		try {
			await deleteDocument(docToDelete.id).unwrap()
			setDocToDelete(null)
		} catch (err) {
			console.error('deleteDocument failed:', err)
		}
	}

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Select
							value={typeFilter}
							onValueChange={(v) => { setTypeFilter(v as DocumentType | 'all'); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('documents.filters.type')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.filters.type')}</SelectItem>
								{DOCUMENT_TYPES.map((dt) => (
									<SelectItem key={dt} value={dt}>{t(DOCUMENT_TYPE_KEYS[dt])}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={propertyFilter}
							onValueChange={(v) => { setPropertyFilter(v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('documents.filters.property')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.filters.allProperties')}</SelectItem>
								{properties.map((p) => (
									<SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={rentalFilter}
							onValueChange={(v) => { setRentalFilter(v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('documents.filters.rental')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.filters.allRentals')}</SelectItem>
								{rentals.map((r) => (
									<SelectItem key={r.id} value={r.id}>
										{r.propertyTitle ?? `${t('documents.filters.rental')} #${r.id.slice(-6)}`}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={statusFilter}
							onValueChange={(v) => { setStatusFilter(v as DocumentStatus | 'all'); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('documents.filters.status')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.filters.status')}</SelectItem>
								{DOCUMENT_STATUSES.map((s) => (
									<SelectItem key={s} value={s}>{t(DOCUMENT_STATUS_KEYS[s])}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<SearchInput
						placeholder={t('documents.search')}
						value={search}
						className="w-[200px]"
						onChange={(e) => { setSearch(e.target.value); setPage(1) }}
					/>
				}
			/>

			{documents.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('documents.empty')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table className="table-fixed">
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead className="w-[30%]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.table.document')}
											</Button>
										</TableHead>
										<TableHead className="w-[20%]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.table.property')}
											</Button>
										</TableHead>
										<TableHead className="w-[22%]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.table.rental')}
											</Button>
										</TableHead>
										<TableHead className="w-[12%]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.table.createdAt')}
											</Button>
										</TableHead>
										<TableHead className="w-[12%] font-[500] text-(--color-muted-foreground)">
											{t('documents.table.status')}
										</TableHead>
										<TableHead className="w-10" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{documents.map((doc) => (
										<TableRow
										key={doc.id}
										className="h-[80px] cursor-pointer"
										onClick={() => void navigate({ to: '/documents/$documentId', params: { documentId: doc.id } })}
									>
											<TableCell className="px-4">
												<span className="text-sm font-[500] text-(--color-foreground) line-clamp-2">{doc.title}</span>
											</TableCell>
											<TableCell className="px-4">
												<div className="flex items-center gap-3 min-w-0">
													{doc.propertyImage ? (
														<img
															src={doc.propertyImage}
															alt={doc.propertyTitle ?? ''}
															className="size-10 shrink-0 rounded-lg object-cover"
														/>
													) : (
														<div className="size-10 shrink-0 rounded-lg bg-(--color-muted)" />
													)}
													<div className="flex flex-col gap-0.5 min-w-0">
														<span className="text-sm font-[500] text-(--color-foreground) truncate">
															{doc.propertyTitle ?? '—'}
														</span>
														<span className="text-sm text-(--color-muted-foreground) truncate">
															{doc.propertyAddress ?? ''}
														</span>
													</div>
												</div>
											</TableCell>
											<TableCell className="px-4">
												{doc.rentalTitle ? (
													<div className="flex flex-col gap-0.5 min-w-0">
														<span className="text-sm font-[500] text-(--color-info) truncate cursor-pointer underline-offset-2 hover:underline">
															{doc.rentalTitle}
														</span>
														<span className="text-sm text-(--color-muted-foreground) truncate">
															{doc.rentalDates ?? ''}
														</span>
													</div>
												) : (
													<span className="text-sm text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground)">
												{formatDate(doc.createdAt)}
											</TableCell>
											<TableCell className="px-4">
												<Badge variant={DOCUMENT_STATUS_VARIANTS[doc.status]} size="sm">
													{t(DOCUMENT_STATUS_KEYS[doc.status])}
												</Badge>
											</TableCell>
											<TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
												<DropdownMenu>
													<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('documents.actions.menu')} />}>
														<EllipsisVerticalIcon className="size-4" />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="w-auto min-w-40">
														<DropdownMenuItem disabled={!doc.fileUrl} onClick={() => handleDownload(doc)}>
															<DownloadIcon />
															{t('documents.actions.download')}
														</DropdownMenuItem>
														<DropdownMenuItem
															disabled={doc.status !== 'DATA_FILLED' && doc.status !== 'DRAFT'}
															onClick={() => handleSign(doc)}
														>
															<PenLineIcon />
															{t('documents.actions.sign')}
														</DropdownMenuItem>
														<DropdownMenuItem
															disabled={doc.status !== 'SIGNED_BY_ME'}
															onClick={() => handleSend(doc)}
														>
															<SendIcon />
															{t('documents.actions.send')}
														</DropdownMenuItem>
														<DropdownMenuSeparator />
														<DropdownMenuItem variant="destructive" onClick={() => setDocToDelete(doc)}>
															<Trash2Icon />
															{t('documents.actions.delete')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={total} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}

			<ConfirmDialog
				open={!!docToDelete}
				onOpenChange={(v) => { if (!v) setDocToDelete(null) }}
				title={t('documents.actions.delete')}
				description={docToDelete ? t('documents.actions.deleteConfirm', { title: docToDelete.title }) : undefined}
				confirmLabel={t('documents.actions.delete')}
				cancelLabel={t('documents.actions.cancel')}
				variant="destructive"
				loading={isDeleting}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	)
}
